/* eslint-disable complexity */
import React, { useContext, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { useNavigate, useParams } from 'react-router-dom';
import {
	Wrapper,
	Typography,
	Row,
	Column,
	PaymentSelection,
	CustomerData,
	DeliveryEmail,
	CheckoutButton,
	PriceInformation,
	MemberInput,
	Contact,
	TermsAndConditions,
	InvoiceCheckbox,
	Button,
	Toast,
	ErrorCrossIcon,
	HintMessage,
	IAGBItem,
} from '@adac-mp/components';
import {
	ButtonState,
	ICustomer,
	initialCustomer,
	OfferContext,
	VignetteContext,
	IOfferContext,
	getPricingInformation,
	CustomerService,
	fetchInitialVignetteData,
	ISepaInfo,
	PaymentMethods,
	setPaymentMethodsTitle,
	onOrderCompleted,
	CartService,
	getCartEntriesSortedByModuleType,
	ICartData,
	getErrorTextFromResponse,
	ICartItem,
	setRouteLangParam,
	countryUrlMap,
	Locales,
	getCartTotalPrice,
	getLabelValue,
	ITypeAgbListFields,
	ITypeVignettenCheckoutFields,
	IVignetteContext,
	getCorrespondingOffer,
	getUrlBasePath,
} from '@adac-mp/shared';
import { useViewport } from '@adac-mp/components/src/hooks/useViewport';
import { gatherProductTrackingData, withTracking } from '@adac-mp/components/src/utils/tracking/tracking';
import { redirectToErrorPage } from '@adac-mp/shared/src/services/axios-config';
import { useFlowId } from '@adac-mp/components/src/hooks/useFlowId';
import { renderTotalPrice } from '../../utils/renderTotalPrice';
import { ProductSummaryCheckout } from './productsSummaryCheckout';

interface IVignetteCheckoutProps {
	agbs?: IAGBItem[] | ITypeAgbListFields;
	countryCode: number;
	hasCategories?: boolean;
	hasFuelType?: boolean;
	hasSpecialPlates?: boolean;
	hasMultipleCountries?: boolean;
}

export const VignetteCheckoutComponent = ({
	agbs = undefined,
	countryCode,
	hasCategories = false,
	hasFuelType = false,
	hasSpecialPlates = false,
	hasMultipleCountries = false,
}: IVignetteCheckoutProps) => {
	const navigate = useNavigate();
	const flowId = useFlowId();
	const isSlovenianFlow = flowId === 7;
	const { viewport } = useViewport();
	const { cartPrice, cartItems, cartInfo, contentfulLabels, updateCart, deleteSelectedProducts } =
		useContext<IVignetteContext>(VignetteContext);
	const {
		allOffers,
		allCountries,
		labels,
		updateVignetteOffers,
		updateAllOffers,
		updateAllCountries,
		updateAllFuels,
		updateTollSectionOffers,
	} = useContext<IOfferContext>(OfferContext);
	const { lang } = useParams();
	const paymentMethod = cartInfo?.paymentMethods[0]?.id;
	const [payment, setPayment] = useState<PaymentMethods | undefined>(
		paymentMethod ? (String(paymentMethod) as PaymentMethods) : undefined,
	);
	const [customer, setCustomer] = useState<ICustomer | undefined>(undefined);
	const [buttonActive, setButtonActive] = useState<ButtonState>('disabled');
	const [customerFilled, setCustomerFilled] = useState<boolean>(false);
	const [memberSuccess, setMemberSuccess] = useState<boolean>(true);
	const [isChecked, setIsChecked] = useState<boolean>(false);
	const [createInvoice, setCreateInvoice] = useState<boolean>(false);
	const [hasMembership, setHasMembership] = useState<boolean>(false);
	const [validateFields, setValidateFields] = useState(false);
	const [sepaInfo, setSepaInfo] = useState<ISepaInfo | undefined>(undefined);
	const [sepaInfoFilled, setSepaInfoFilled] = useState<boolean>(false);
	const [error, setError] = useState<string | undefined>(undefined);
	const [apiError, setApiError] = useState<string | undefined>(undefined);
	const errorMsgRef = useRef<HTMLDivElement | null>(null);
	const countryUrl = countryUrlMap[countryCode];
	const currentLabels = contentfulLabels as ITypeVignettenCheckoutFields;
	const hasMembershipBenefit = Boolean(
		cartItems.find(({ offer }) => offer.supplierId === 7 || offer.supplierId === 14),
	);

	const totalPrice = getCartTotalPrice(
		cartPrice,
		currentLabels,
		cartInfo?.forceInvoice && cartItems.every(({ offer }) => offer.moduleTypeId === 4),
	);
	const discount = cartInfo?.sumDiscount?.amount;
	const paymentLabel = cartInfo?.paymentMethods.find(({ id }) => String(id) === payment)?.name ?? String(payment);

	const handleCartErrorCode = (res: ICartData) => {
		if (res.code === 401) {
			navigate(getUrlBasePath(`/session-expired${setRouteLangParam(lang)}`), { replace: true });
			deleteSelectedProducts();
		} else if (res.redirectToErrorPage) {
			redirectToErrorPage(res.errors);
			deleteSelectedProducts();
		} else {
			const errorText = getErrorTextFromResponse(res.errors, getLabelValue('mp.generalError', labels));
			setApiError(errorText);
		}
	};

	const handleCartResponse = (res: ICartData) => {
		if (res.code === 200 && res.cart && res.cart.cartDetails.length > 0) {
			const cartPaymentMethod = res.cart.paymentMethods[0].id;
			setPayment(cartPaymentMethod ? (String(cartPaymentMethod) as PaymentMethods) : undefined);
			updateCart(res.cart);
			sessionStorage.setItem('cartHash', res.cart.cartHash);
			const offers = res.cart?.cartDetails.map((item) => item.offer);
			gatherProductTrackingData({ offers, status: 'checkout', additionalData: { checkOutStep: 1 } });
		} else {
			handleCartErrorCode(res);
		}
	};

	const resetCartMembershipBenefits = (cart: ICartItem[]) => {
		const memberProduct = cart.some(({ adacMember }) => adacMember);
		if (!memberProduct) return;
		const cartHash = sessionStorage.getItem('cartHash');
		const token = sessionStorage.getItem('cartToken');
		if (cartHash && token) {
			CartService.updateCartWithMembership({ cartHash, member: false }, token).then((res) =>
				handleCartResponse(res),
			);
		}
	};

	const updateMembership = (isMember: boolean) => {
		if (hasMembership === isMember) return;
		const cartHash = sessionStorage.getItem('cartHash');
		const token = sessionStorage.getItem('cartToken');
		if (cartHash && token) {
			CartService.updateCartWithMembership({ cartHash, member: isMember }, token).then((res) => {
				if (res.code === 200 && res.cart && res.cart.cartDetails.length > 0) {
					updateCart(res.cart);
				} else {
					handleCartErrorCode(res);
				}
			});
			setHasMembership(isMember);
		}
	};

	useEffect(() => {
		const offerHash = sessionStorage.getItem('offerHash');
		const customerHash = sessionStorage.getItem('customerHash');
		const customerToken = sessionStorage.getItem('customerToken');
		const cartHash = sessionStorage.getItem('cartHash');
		const token = sessionStorage.getItem('cartToken');

		if (!offerHash && !cartHash) {
			navigate(getUrlBasePath(`/${countryUrl}/configure${setRouteLangParam(lang)}`));
			return;
		}

		if (customerHash && customerToken) {
			CustomerService.get(customerHash, customerToken).then((res) => {
				setCustomer(res.customer);
				updateMembership(Boolean(res.customer.person.memberNo));
			});
		} else {
			setCustomer(initialCustomer);
		}

		if (cartHash && token && (cartItems.length === 0 || lang)) {
			CartService.getCartInfo({ body: { cartHash }, token, lang: lang as Locales }).then((res) => {
				if (res.code === 200 && res.cart && res.cart.cartDetails.length > 0) {
					const memberProduct = res.cart.cartDetails.some(({ adacMember }) => adacMember);
					if (memberProduct && !customerHash) {
						CartService.updateCartWithMembership({ cartHash, member: false }, token).then((response) =>
							handleCartResponse(response),
						);
					} else {
						handleCartResponse(res);
					}
				} else {
					handleCartErrorCode(res);
				}
			});
		} else {
			const offers = cartItems.map((item) => item.offer);
			gatherProductTrackingData({ offers, status: 'checkout', additionalData: { checkOutStep: 1 } });
		}

		if (allOffers.length === 0 || lang) {
			fetchInitialVignetteData(countryCode, {
				navigate,
				lang: lang as Locales,
				updateVignetteOffers,
				updateAllOffers,
				updateAllCountries,
				updateAllFuels: hasFuelType ? updateAllFuels : undefined,
				updateTollSectionOffers,
			});
		}
	}, [lang]);

	useEffect(() => {
		setButtonActive(
			customerFilled && memberSuccess && isChecked && (!(payment === '3') || (payment === '3' && sepaInfoFilled))
				? 'active'
				: 'disabled',
		);
	}, [customerFilled, memberSuccess, isChecked, sepaInfoFilled, payment]);

	useEffect(() => {
		if (hasMembershipBenefit && cartInfo?.sumDiscount?.amount) {
			if (hasMembership) {
				sessionStorage.setItem('memberDiscount', cartInfo?.sumDiscount?.amount.replace(',', '.'));
			} else {
				sessionStorage.removeItem('memberDiscount');
			}
		}
	}, [hasMembershipBenefit, hasMembership, cartInfo]);

	const getLabelByModuleType = (moduleType: string) => {
		if (!currentLabels?.priceDetails) {
			return `Digitale ${moduleType}`;
		}
		return moduleType === 'Vignette'
			? currentLabels.priceDetails.digitalVignettesHeadline
			: currentLabels.priceDetails.digitalTollSectionHeadline;
	};

	const cartPrices = getCartEntriesSortedByModuleType(cartItems, allOffers).map(([moduleType, items]) => {
		return {
			title: getLabelByModuleType(moduleType),
			details: items.map(({ offer }) => {
				const correspondingOffer = getCorrespondingOffer(allOffers, offer);
				const pricings = getPricingInformation(offer, labels, currentLabels, correspondingOffer, hasMembership);
				return pricings.map((priceItem) => priceItem);
			}),
		};
	});

	const pricingElements = {
		label: currentLabels?.priceDetails?.priceDetailsHeadline || 'Preisdetails',
		totalOfferPrice: totalPrice.amount,
		additionalInfo: cartPrices,
	};

	const handleClick = () => {
		setValidateFields(true);
		const scrollPos = viewport === 'desktop' ? 355 : 840;
		window.scrollTo({ top: scrollPos, behavior: 'smooth' });
	};

	useEffect(() => {
		if (error && errorMsgRef.current) {
			const rect = errorMsgRef.current.getBoundingClientRect();
			window.scrollTo({
				top: rect.top + window.scrollY - rect.height,
				behavior: 'smooth',
			});
		}
	}, [error, errorMsgRef.current]);

	const renderErrorMessage = () =>
		error && <HintMessage ref={errorMsgRef} className="mb-5" variant="error" hints={[{ message: error }]} />;

	const renderCheckoutButton = () => (
		<>
			<TermsAndConditions
				updateButtonState={setIsChecked}
				validateFields={validateFields}
				agbs={currentLabels?.agbs || agbs}
			/>
			{payment && (
				<CheckoutButton
					variant="vignette"
					paymentLabel={paymentLabel}
					label={getLabelValue('mp.confirmPaymentButton', labels)}
					paymentLabels={{ creditCard: currentLabels?.creditCardDetails, sepa: currentLabels?.sepaMandat }}
					paymentMethod={payment}
					countryUrl={countryUrl}
					buttonState={buttonActive}
					customer={customer}
					sepaData={sepaInfo}
					setError={setError}
					updateButtonState={setButtonActive}
					onOrderCompleted={onOrderCompleted(navigate)}
					onClick={handleClick}
				/>
			)}
		</>
	);

	const renderPaymentMethods = () =>
		cartInfo ? (
			<PaymentSelection
				paymentMethods={cartInfo.paymentMethods}
				value={payment}
				validateFields={validateFields}
				setValue={setPayment}
				updateSepaInfo={setSepaInfo}
				setSepaInfoFilled={setSepaInfoFilled}
				showHint={totalPrice.decimalAmount === 0}
				labels={currentLabels?.personalData}
			/>
		) : (
			<Skeleton width="100%" height={60} className="rounded" />
		);

	const renderCheckoutForm = () => (
		<>
			{customer && (
				<>
					{createInvoice || payment === '3' || cartInfo?.forceInvoice ? (
						<CustomerData
							hasMembershipBenefit={hasMembershipBenefit}
							labels={currentLabels?.personalData}
							variant="vignette"
							customer={customer}
							allCountries={hasMultipleCountries ? allCountries : undefined}
							validateFields={validateFields}
							setCustomerFilled={setCustomerFilled}
							updateCustomer={setCustomer}
						/>
					) : (
						<DeliveryEmail
							hasMembershipBenefit={hasMembershipBenefit}
							labels={currentLabels?.personalData}
							customer={customer}
							validateFields={validateFields}
							setCustomerFilled={setCustomerFilled}
							updateCustomer={setCustomer}
						/>
					)}
					{payment && viewport === 'mobile' && renderCheckoutButton()}
				</>
			)}
		</>
	);

	return (
		<>
			<Wrapper minSpace>
				{viewport === 'mobile' && (
					<Column className="md:basis-full mb-4">
						<Typography size="h2" component="h1" variant="header" color="title" className="pl-4">
							{currentLabels?.checkoutHeadline || 'Jetzt bestellen'}
						</Typography>
					</Column>
				)}
				<Row className="md:flex-row-reverse">
					<Column className="order-2 md:basis-7/12">
						{viewport === 'desktop' && (
							<Typography size="h2" component="h1" variant="header" color="title" className="pl-4 mb-5.5">
								{currentLabels?.checkoutHeadline || 'Jetzt bestellen'}
							</Typography>
						)}
						{viewport === 'desktop' && renderErrorMessage()}
						<Typography color="title" variant="header" size="xxl" component="h3" className="px-4 mb-4">
							{cartInfo &&
								setPaymentMethodsTitle(cartInfo.paymentMethods, [
									currentLabels?.checkoutPayment,
									currentLabels?.checkoutChoosePayment,
								])}
						</Typography>
						{!cartInfo?.forceInvoice && (
							<InvoiceCheckbox
								createInvoice={createInvoice}
								setCreateInvoice={setCreateInvoice}
								label={currentLabels?.checkboxInvoice}
							/>
						)}
						{renderPaymentMethods()}
						{renderCheckoutForm()}
					</Column>
					<Column className="order-1 md:basis-5/12 md:sticky md:self-start md:top-7">
						{viewport === 'mobile' && renderErrorMessage()}
						<ProductSummaryCheckout
							hasFuelType={hasFuelType}
							hasCategories={hasCategories}
							hasSpecialPlates={hasSpecialPlates}
						/>
						{hasMembershipBenefit && customer && (
							<div className="mb-5">
								<MemberInput
									hasMembershipBenefit
									customer={customer}
									discount={discount}
									updateMembership={updateMembership}
									setCustomer={setCustomer}
									setMemberSuccess={setMemberSuccess}
								/>
							</div>
						)}
						<PriceInformation
							className={`bg-gray-600 px-4 mb-5 ${
								isSlovenianFlow || (cartInfo?.flexProduct && flowId !== 0)
									? 'rounded-t-none rounded-b pb-3.5 pt-3'
									: 'rounded py-3.5'
							}`}
							expandable={cartInfo?.flexProduct && flowId !== 0}
							hasMembership={hasMembership}
							hasMembershipBenefit={hasMembershipBenefit}
							totalPrice={
								isSlovenianFlow
									? totalPrice
									: renderTotalPrice({
											hasMembershipBenefit,
											price: totalPrice,
											pricingElements,
											hasMembership,
											showSubHeadline: true,
									  })
							}
						/>
						<Button
							fullWidth
							data-cy="button: add-product"
							variant="secondary"
							className="mb-5 md:mb-0"
							tracking={{
								funnel_click: {
									label: getLabelValue('mp.addProduct', labels) || 'Produkt hinzufügen',
									element: 'Checkout',
								},
							}}
							onClick={() => {
								resetCartMembershipBenefits(cartItems);
								navigate(getUrlBasePath(`/${countryUrl}/configure${setRouteLangParam(lang)}`));
							}}>
							{getLabelValue('mp.addProduct', labels) || 'Produkt hinzufügen'}
						</Button>
						{payment && viewport === 'desktop' && renderCheckoutButton()}
					</Column>
				</Row>
			</Wrapper>
			<Contact />
			{apiError && (
				<Toast content={apiError} icon={<ErrorCrossIcon />} onToastFinished={() => setApiError(undefined)} />
			)}
		</>
	);
};

export const VignetteCheckout = withTracking(VignetteCheckoutComponent);
